import { useState } from "react"
import { Loader2, MessageSquareText, Phone, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"

export type NachbereitungContext = {
  situation: string
  verlauf: string
  frage: string
}

/**
 * Der erste Beitrag, mit dem der Thread angelegt wird.
 *
 * Bewusst in ihren Worten und nicht als Auftrag an den Coach formuliert: die
 * Nachbereitung beginnt damit, dass sie erzählt. Der letzte Satz hält den Coach
 * davon ab, sofort zu bewerten — erst verstehen, dann challengen.
 */
export function nachbereitungPrompt(context: NachbereitungContext) {
  return `Ich möchte ein Gespräch nachbesprechen, das ich geführt habe.\n\nWorum ging es: ${context.situation.trim()}\n\nSo ist es gelaufen: ${context.verlauf.trim()}${
    context.frage.trim() ? `\n\nDas lässt mich nicht los: ${context.frage.trim()}` : ""
  }\n\nFrag mich nach, bevor du etwas einschätzt.`
}

/**
 * Der Einstieg in die Nachbereitung eines real geführten Gesprächs.
 *
 * Drei Felder, und keines fragt nach dem Kunden: es geht um ihre
 * Gesprächsführung, nicht um den Fall. Der Datenschutzhinweis steht nicht im
 * Kleingedruckten, sondern über dem ersten Feld — das ist die Bedingung, unter
 * der die Sparkassen diesen Einstieg überhaupt mittragen.
 */
export function NachbereitungIntake({
  coachName,
  onStart,
  pending = false,
}: {
  coachName: string
  onStart: (context: NachbereitungContext, channel: "voice" | "chat") => void
  pending?: boolean
}) {
  const [context, setContext] = useState<NachbereitungContext>({
    situation: "",
    verlauf: "",
    frage: "",
  })
  const set = (key: keyof NachbereitungContext, value: string) =>
    setContext((current) => ({ ...current, [key]: value }))

  const bereit = !!context.situation.trim() && !!context.verlauf.trim()

  return (
    <div className="mx-auto h-full w-full max-w-3xl overflow-y-auto px-1 py-2 sm:py-6">
      <p className="mb-3 flex items-center gap-2 text-sm font-medium text-primary">
        <MessageSquareText size={18} /> Nachbereitung
      </p>
      <h1 className="font-display text-2xl leading-tight font-medium sm:text-3xl">
        Ein Gespräch, das dich noch beschäftigt.
      </h1>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
        Erzähl {coachName}, wie es gelaufen ist. Ihr schaut gemeinsam auf deine
        Gesprächsführung und darauf, was du ins nächste Gespräch mitnimmst.
      </p>

      {/* Über dem ersten Feld, nicht im Kleingedruckten darunter: der Hinweis
          ist die Bedingung für diesen Einstieg, nicht seine Fußnote. */}
      <div className="mt-5 flex items-start gap-3 rounded-xl border border-[var(--rule)] bg-card p-4">
        <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
          <ShieldCheck size={16} aria-hidden />
        </span>
        <p className="text-[13px] leading-relaxed text-muted-foreground">
          Schreib bitte anonymisiert, also ohne Namen, Kontaktdaten, Konto- oder
          Vertragsnummern. Hier geht es um <em>dein</em> Gespräch, nicht um den
          Kunden: nichts davon wird einem Kundendatensatz zugeordnet, und{" "}
          {coachName} bewertet keinen realen Fall.
        </p>
      </div>

      <form
        className="mt-7 space-y-6"
        onSubmit={(event) => {
          event.preventDefault()
          if (bereit) onStart(context, "voice")
        }}
      >
        <section className="space-y-2.5">
          <label
            htmlFor="nachbereitung-situation"
            className="block text-base font-medium"
          >
            Worum ging es in dem Gespräch?
          </label>
          <p
            id="nachbereitung-situation-hint"
            className="text-sm text-muted-foreground"
          >
            Anlass und Rahmen genügen, keine Angaben zur Person.
          </p>
          <Textarea
            id="nachbereitung-situation"
            value={context.situation}
            onChange={(e) => set("situation", e.target.value)}
            required
            maxLength={600}
            rows={3}
            aria-describedby="nachbereitung-situation-hint"
            placeholder="Zum Beispiel: Ein Altersvorsorge-Gespräch mit jemandem Anfang dreißig, erster Termin nach einem Jobwechsel."
            className="bg-card p-3"
          />
        </section>

        <section className="space-y-2.5">
          <label
            htmlFor="nachbereitung-verlauf"
            className="block text-base font-medium"
          >
            Wie ist es gelaufen?
          </label>
          <Textarea
            id="nachbereitung-verlauf"
            value={context.verlauf}
            onChange={(e) => set("verlauf", e.target.value)}
            required
            maxLength={1000}
            rows={4}
            placeholder="Zum Beispiel: Der Einstieg lief gut, aber bei der Vorsorgelücke habe ich zu viel geredet. Am Ende gab es keinen konkreten nächsten Schritt."
            className="bg-card p-3"
          />
        </section>

        <section className="space-y-2.5">
          <label
            htmlFor="nachbereitung-frage"
            className="block text-base font-medium"
          >
            Was lässt dich daran nicht los?{" "}
            <span className="text-sm font-normal text-muted-foreground">
              Optional
            </span>
          </label>
          <Textarea
            id="nachbereitung-frage"
            value={context.frage}
            onChange={(e) => set("frage", e.target.value)}
            maxLength={400}
            rows={2}
            placeholder="Zum Beispiel: Ich frage mich, ob ich den Abschluss zu früh angesprochen habe."
            className="bg-card p-3"
          />
        </section>

        <div className="flex flex-col gap-4 border-t pt-5 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
            {coachName} war nicht dabei und fragt nach, bevor er etwas einordnet.
          </p>
          <div className="flex flex-col gap-2 sm:items-end">
            <Button
              type="submit"
              className="min-h-11 shrink-0"
              disabled={pending || !bereit}
            >
              {pending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Phone size={16} />
              )}
              {pending ? "Wird vorbereitet …" : "Live-Gespräch starten"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={pending || !bereit}
              onClick={() => onStart(context, "chat")}
            >
              Lieber im Chat nachbesprechen
            </Button>
          </div>
        </div>
      </form>
    </div>
  )
}
